<template>
  <v-main>
    <v-container fluid>
      <!-- Unified Upload Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <!-- Page Header Section -->
            <!-- Page Header -->
            <page-header
              title="Bulk Product Upload"
              subtitle="Upload multiple products using Excel file with validation and preview"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <v-btn
                      color="info"
                      elevation="1"
                      small
                      @click="downloadTemplate"
                      :loading="isDownloadingTemplate"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-download</v-icon>
                      Download Template
                    </v-btn>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      small
                      @click="$router.back()"
                      class="px-3"
                    >
                      <v-icon left x-small>mdi-arrow-left</v-icon>
                      Back to Products
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Section Divider -->
            <v-divider class="mx-4"></v-divider>

            <!-- Upload Steps Section -->
            <v-card-text class="pa-4 pb-2">
              <v-stepper v-model="currentStep" non-linear>
                <v-stepper-header>
                  <v-stepper-step step="1" :complete="currentStep > 1" editable>
                    Upload File
                  </v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step step="2" :complete="currentStep > 2" :editable="hasPreviewData">
                    Preview & Validate
                  </v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step step="3" :complete="currentStep > 3" :editable="canConfirm">
                    Confirm & Save
                  </v-stepper-step>
                </v-stepper-header>
              </v-stepper>
            </v-card-text>

            <!-- Section Divider -->
            <v-divider class="mx-4"></v-divider>
            <!-- Step 1: File Upload -->
            <div v-show="currentStep === 1">
              <v-card-text class="pa-4 pt-2">
                <v-row>
                  <!-- File Upload Area -->
                  <v-col cols="12" md="8">
                    <v-file-input
                      v-model="selectedFile"
                      label="Select Excel File"
                      placeholder="Choose .xlsx or .xls file"
                      prepend-icon="mdi-file-excel"
                      accept=".xlsx,.xls"
                      show-size
                      :rules="fileRules"
                      :error-messages="fileErrors"
                      @change="handleFileSelection"
                      class="mb-4"
                    ></v-file-input>

                    <v-textarea
                      v-model="uploadNotes"
                      label="Upload Notes (Optional)"
                      placeholder="Add any notes about this upload..."
                      rows="3"
                      outlined
                      hide-details
                      class="mb-4"
                    ></v-textarea>

                    <div class="d-flex justify-end">
                      <v-btn
                        color="primary"
                        :disabled="!selectedFile || isUploading"
                        :loading="isUploading"
                        @click="uploadAndPreview"
                        class="px-6"
                      >
                        <v-icon left>mdi-cloud-upload</v-icon>
                        Upload & Preview
                      </v-btn>
                    </div>
                  </v-col>

                  <!-- Instructions -->
                  <v-col cols="12" md="4">
                    <v-card outlined>
                      <v-card-title class="pb-2">
                        <v-icon color="info" class="mr-2">mdi-information</v-icon>
                        Upload Instructions
                      </v-card-title>
                      <v-card-text>
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="success">mdi-check</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Download template first
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="success">mdi-check</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Fill required fields (marked with *)
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="info">mdi-layers</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Supports nested product & variant creation
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="info">mdi-account-multiple</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Can map products to sellers
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="info">mdi-file</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Max file size: 10MB
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </div>

            <!-- Section Divider -->
            <v-divider v-if="currentStep > 1" class="mx-4 my-2"></v-divider>

            <!-- Step 2: Preview & Validation -->
            <div v-show="currentStep === 2 && hasPreviewData">
              <v-card-text class="pa-4 pt-2">
                <!-- Validation Summary -->
                <v-row class="mb-4">
                  <v-col cols="12" md="3">
                    <v-card outlined>
                      <v-card-text class="text-center pa-3">
                        <v-icon size="32" color="primary">mdi-file-table</v-icon>
                        <div class="text-h6 font-weight-bold">{{ validationSummary.total_rows }}</div>
                        <div class="text-caption">Total Rows</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-card outlined>
                      <v-card-text class="text-center pa-3">
                        <v-icon size="32" color="success">mdi-check-circle</v-icon>
                        <div class="text-h6 font-weight-bold text-success">{{ validationSummary.valid_rows }}</div>
                        <div class="text-caption">Valid Rows</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-card outlined>
                      <v-card-text class="text-center pa-3">
                        <v-icon size="32" color="error">mdi-alert-circle</v-icon>
                        <div class="text-h6 font-weight-bold text-error">{{ validationSummary.invalid_rows }}</div>
                        <div class="text-caption">Invalid Rows</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-card outlined>
                      <v-card-text class="text-center pa-3">
                        <v-icon size="32" :color="validationSummary.success_rate >= 80 ? 'success' : validationSummary.success_rate >= 60 ? 'warning' : 'error'">
                          mdi-chart-line
                        </v-icon>
                        <div class="text-h6 font-weight-bold" :class="validationSummary.success_rate >= 80 ? 'text-success' : validationSummary.success_rate >= 60 ? 'text-warning' : 'text-error'">
                          {{ validationSummary.success_rate }}%
                        </div>
                        <div class="text-caption">Success Rate</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Preview Data Table -->
                <v-card outlined class="mb-4">
                  <v-card-title class="pb-2">
                    <v-icon color="primary" class="mr-2">mdi-table</v-icon>
                    Data Preview (First 20 rows)
                  </v-card-title>
                  <v-data-table
                    :headers="previewHeaders"
                    :items="previewData"
                    :items-per-page="10"
                    class="elevation-0"
                    fixed-header
                    height="400"
                  >
                    <template #[`item.row_number`]="{ item }">
                      <status-chip :color="item.is_valid ? 'success' : 'error'" :label="String(item.row_number)" />
                    </template>
                    <template #[`item.name`]="{ item }">
                      <span :class="item.is_valid ? '' : 'text-error'">
                        {{ item.data.name || 'N/A' }}
                      </span>
                    </template>
                    <template #[`item.brand_name`]="{ item }">
                      <span :class="item.is_valid ? '' : 'text-error'">
                        {{ item.data.brand_name || 'N/A' }}
                      </span>
                    </template>
                    <template #[`item.hsn_code`]="{ item }">
                      {{ item.data.hsn_code || 'N/A' }}
                    </template>
                    <template #[`item.stock`]="{ item }">
                      {{ item.data.stock || 0 }}
                    </template>
                    <template #[`item.is_active`]="{ item }">
                      <status-chip :color="item.data.is_active ? 'success' : 'grey'" :label="String(item.data.is_active ? 'Active' : 'Inactive')" />
                    </template>
                  </v-data-table>
                </v-card>

                <!-- Validation Errors (if any) -->
                <v-card v-if="validationSummary.has_errors" outlined color="error">
                  <v-card-title class="pb-2 error white--text">
                    <v-icon color="white" class="mr-2">mdi-alert</v-icon>
                    Validation Errors
                  </v-card-title>
                  <v-card-text class="pa-0">
                    <v-expansion-panels>
                      <v-expansion-panel v-for="(error, index) in validationErrors.slice(0, 10)" :key="index">
                        <v-expansion-panel-header>
                          <strong>Row {{ error.row_number }}:</strong>
                          <span class="ml-2">{{ error.data.name || 'Unnamed Product' }}</span>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-list dense>
                            <v-list-item v-for="(messages, field) in error.errors" :key="field">
                              <v-list-item-icon>
                                <v-icon small color="error">mdi-alert-circle</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="text-caption">
                                  <strong>{{ field }}:</strong> {{ Array.isArray(messages) ? messages.join(', ') : messages }}
                                </v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                    <div v-if="validationErrors.length > 10" class="pa-3 text-center">
                      <span class="text-caption">
                        Showing first 10 errors. Total: {{ validationErrors.length }} errors.
                      </span>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Action Buttons -->
                <div class="d-flex justify-space-between mt-4">
                  <v-btn
                    outlined
                    @click="currentStep = 1"
                  >
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back to Upload
                  </v-btn>
                  <div>
                    <v-btn
                      v-if="validationSummary.has_errors"
                      color="warning"
                      @click="proceedWithErrors"
                      :disabled="validationSummary.valid_rows === 0"
                      class="mr-2"
                    >
                      <v-icon left>mdi-alert</v-icon>
                      Proceed with Valid Rows ({{ validationSummary.valid_rows }})
                    </v-btn>
                    <v-btn
                      v-else
                      color="success"
                      @click="currentStep = 3"
                    >
                      <v-icon left>mdi-check</v-icon>
                      Proceed to Confirmation
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </div>

            <!-- Section Divider -->
            <v-divider v-if="currentStep > 2" class="mx-4 my-2"></v-divider>

            <!-- Step 3: Confirm & Save -->
            <div v-show="currentStep === 3 && canConfirm">
              <v-card-text class="pa-4 pt-2">
                <v-alert
                  :type="validationSummary.has_errors ? 'warning' : 'success'"
                  outlined
                  class="mb-4"
                >
                  <div v-if="validationSummary.has_errors">
                    <strong>Partial Upload:</strong> 
                    {{ validationSummary.valid_rows }} out of {{ validationSummary.total_rows }} products will be created.
                    {{ validationSummary.invalid_rows }} products have validation errors and will be skipped.
                  </div>
                  <div v-else>
                    <strong>Full Upload:</strong>
                    All {{ validationSummary.total_rows }} products passed validation and will be created.
                  </div>
                </v-alert>

                <v-card outlined class="mb-4">
                  <v-card-title class="pb-2">
                    <v-icon color="info" class="mr-2">mdi-information</v-icon>
                    What happens next?
                  </v-card-title>
                  <v-card-text>
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="primary">mdi-database</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            Valid products will be created in your inventory
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="primary">mdi-history</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            Upload activity will be logged for future reference
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="primary">mdi-email</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            You'll receive a summary report with details
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>

                <div class="d-flex justify-space-between">
                  <v-btn
                    outlined
                    @click="currentStep = 2"
                  >
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back to Preview
                  </v-btn>
                  <v-btn
                    color="success"
                    :loading="isConfirming"
                    @click="confirmUpload"
                    class="px-6"
                  >
                    <v-icon left>mdi-check</v-icon>
                    Confirm & Create Products
                  </v-btn>
                </div>
              </v-card-text>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600" persistent>
        <v-card>
          <v-card-title class="success white--text">
            <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
            Upload Completed Successfully!
          </v-card-title>
          <v-card-text class="pa-4">
            <div class="text-center">
              <v-icon size="64" color="success" class="mb-3">mdi-package-variant</v-icon>
              <h3 class="mb-3">Products Created Successfully</h3>
              
              <v-row class="mb-3">
                <v-col cols="6">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <div class="text-h4 font-weight-bold text-success">{{ uploadResult.created_count }}</div>
                      <div class="text-caption">Products Created</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <div class="text-h4 font-weight-bold text-error">{{ uploadResult.failed_count }}</div>
                      <div class="text-caption">Products Failed</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
              
              <p class="text-body-2 grey--text">
                Your products have been successfully added to the inventory.
                You can view them in the products list or upload history.
              </p>
            </div>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              outlined
              @click="uploadAnother"
            >
              Upload Another File
            </v-btn>
            <v-btn
              color="primary"
              @click="goToProducts"
            >
              View Products
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "ProductUpload",
  data() {
    return {
      currentStep: 1,
      selectedFile: null,
      uploadNotes: '',
      isDownloadingTemplate: false,
      isUploading: false,
      isConfirming: false,
      
      // Preview data
      hasPreviewData: false,
      previewData: [],
      validationSummary: {
        total_rows: 0,
        valid_rows: 0,
        invalid_rows: 0,
        success_rate: 0,
        has_errors: false,
      },
      validationErrors: [],
      uploadId: null,
      
      // Success dialog
      showSuccessDialog: false,
      uploadResult: {
        created_count: 0,
        failed_count: 0,
        creation_details: null,
      },
      
      // Form validation
      fileRules: [
        v => !!v || 'Excel file is required',
        v => !v || v.size < 10485760 || 'File size should be less than 10MB',
        v => !v || ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'].includes(v.type) || 'File must be .xlsx or .xls format',
      ],
      fileErrors: [],
      
      // Table headers for preview
      previewHeaders: [
        { text: 'Row', value: 'row_number', width: '80px', sortable: false },
        { text: 'Product Name & Description', value: 'name', width: '250px', sortable: false },
        { text: 'Variant Info', value: 'variant_info', width: '150px', sortable: false },
        { text: 'Seller Info', value: 'seller_info', width: '150px', sortable: false },
        { text: 'Pricing', value: 'pricing', width: '120px', sortable: false },
        { text: 'Stock', value: 'stock', width: '100px', sortable: false },
        { text: 'Status', value: 'is_active', width: '100px', sortable: false },
      ],
    };
  },
  
  computed: {
    canConfirm() {
      return this.hasPreviewData && this.validationSummary.valid_rows > 0;
    },
  },
  
  methods: {
    async downloadTemplate() {
      this.isDownloadingTemplate = true;
      try {
        const response = await axios.get('/products/upload/template', {
          responseType: 'blob',
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `product_upload_template_${new Date().toISOString().slice(0, 10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        this.$toast.success('Template downloaded successfully');
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$store.dispatch('errors/setError', {
          message: 'Error downloading template',
        });
      } finally {
        this.isDownloadingTemplate = false;
      }
    },
    
    handleFileSelection() {
      this.fileErrors = [];
    },
    
    async uploadAndPreview() {
      if (!this.selectedFile) return;
      
      this.isUploading = true;
      this.fileErrors = [];
      
      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        if (this.uploadNotes) {
          formData.append('notes', this.uploadNotes);
        }
        
        const response = await axios.post('/products/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (response.data.success) {
          this.uploadId = response.data.data.upload_id;
          this.validationSummary = response.data.data.validation_summary;
          this.previewData = response.data.data.preview_data;
          this.validationErrors = response.data.data.errors;
          this.hasPreviewData = true;
          this.currentStep = 2;
          
          this.$toast.success('File uploaded and validated successfully');
        } else {
          this.$store.dispatch('errors/setError', {
            message: response.data.message,
          });
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        if (error.response && error.response.status === 422) {
          this.fileErrors = error.response.data.errors.file || [];
        } else {
          this.$store.dispatch('errors/setError', {
            message: 'Error uploading file',
          });
        }
      } finally {
        this.isUploading = false;
      }
    },
    
    proceedWithErrors() {
      this.currentStep = 3;
    },
    
    async confirmUpload() {
      if (!this.uploadId) return;
      
      this.isConfirming = true;
      
      try {
        const response = await axios.post(`/products/upload/${this.uploadId}/confirm`);
        
        if (response.data.success) {
          this.uploadResult.created_count = response.data.data.created_products.length;
          this.uploadResult.failed_count = response.data.data.failed_products.length;
          this.showSuccessDialog = true;
        } else {
          this.$store.dispatch('errors/setError', {
            message: response.data.message,
          });
        }
      } catch (error) {
        console.error('Error confirming upload:', error);
        this.$store.dispatch('errors/setError', {
          message: 'Error confirming upload',
        });
      } finally {
        this.isConfirming = false;
      }
    },
    
    uploadAnother() {
      this.showSuccessDialog = false;
      this.resetForm();
    },
    
    goToProducts() {
      this.showSuccessDialog = false;
      this.$router.push('/products');
    },
    
    resetForm() {
      this.currentStep = 1;
      this.selectedFile = null;
      this.uploadNotes = '';
      this.hasPreviewData = false;
      this.previewData = [];
      this.validationSummary = {
        total_rows: 0,
        valid_rows: 0,
        invalid_rows: 0,
        success_rate: 0,
        has_errors: false,
      };
      this.validationErrors = [];
      this.uploadId = null;
      this.fileErrors = [];
    },
  },
};
</script>

<style scoped>
.v-stepper >>> .v-stepper__step--active .v-stepper__label {
  text-shadow: none;
}

.v-stepper >>> .v-stepper__step--complete .v-icon {
  color: #4CAF50 !important;
}

.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.text-error {
  color: #F44336 !important;
}

.text-success {
  color: #4CAF50 !important;
}

.text-warning {
  color: #FF9800 !important;
}
</style>